import { BRAND, EXPERIENCE } from "@/content";
import { Award, GraduationCap, Briefcase, MapPin, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="mx-auto max-w-[1440px] px-6 lg:px-10 pt-16 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <span className="eyebrow">Founder</span>
          <h1 className="mt-4 text-4xl lg:text-6xl tracking-[-0.03em] font-semibold text-slate-900 leading-[1.02]">
            {BRAND.founder}
          </h1>
          <div className="mt-3 text-[15px] text-slate-600">{BRAND.founderRole} · Indusynq Consulting · {BRAND.city}</div>
          <p className="mt-8 text-[17px] text-slate-600 leading-[1.7] max-w-2xl">
            Business leader with three decades of experience in Sales & Marketing, Manufacturing, Industrial Services and Global
            Supply Chains — including 8+ years in P&L ownership roles managing operations of ₹450 Cr+. A proven track record
            scaling industrial businesses, driving revenue growth and delivering operational transformation across Mechanical
            Power Transmission in the Heavy Industry sector.
          </p>
          <p className="mt-4 text-[17px] text-slate-600 leading-[1.7] max-w-2xl">
            Expertise in building high-performance teams, setting up greenfield manufacturing facilities, localising advanced
            engineering technologies and developing service-led business expansion. Recognised for measurable impact — 80%
            revenue growth in metals aftermarket services, 300% enterprise expansion contributing to Unicorn status, and
            strategic cost optimisation across global supply networks managing portfolios exceeding ₹1,700 Cr.
          </p>
        </div>
        <div className="lg:col-span-5">
          <div className="relative rounded-2xl overflow-hidden aspect-[4/5] bg-slate-100">
            <img
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_constraint-to-cash/artifacts/jglpgnyy_IMG_4695.jpeg"
              alt={BRAND.founder}
              className="absolute inset-0 h-full w-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Competencies */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10 py-14">
          <span className="eyebrow">Core Competencies</span>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 text-[15px] text-slate-800">
            {[
              "P&L Leadership",
              "Supply Chain Transformation",
              "Large-Scale Team Leadership",
              "Manufacturing & Services Ops",
              "Enterprise Sales & Distribution",
              "Greenfield & Capex Projects",
              "Global Tech Transfer & Localisation",
              "Digital Transformation & Industry 4.0",
            ].map((c, i) => (
              <div key={i} data-testid={`competency-${i}`} className="flex items-center gap-3 py-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#712ae2]" />
                <span>{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Timeline */}
      <section className="mx-auto max-w-[1440px] px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="eyebrow">Professional Experience</span>
            <h2 className="mt-3 text-3xl lg:text-5xl tracking-tight font-semibold text-slate-900">
              A career built on plant floors.
            </h2>
          </div>
          <Briefcase className="h-8 w-8 text-slate-300 hidden md:block" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-8">
          <div className="lg:col-span-12">
            <ol className="relative border-l-2 border-slate-200 space-y-8 ml-2">
              {EXPERIENCE.map((e, i) => (
                <li key={i} data-testid={`exp-${i}`} className="pl-8 relative">
                  <span className="absolute -left-[7px] top-2 h-3 w-3 rounded-full bg-slate-900 ring-4 ring-white" />
                  <div className="text-xs tracking-[0.15em] uppercase text-slate-500 font-mono-tabular">{e.years}</div>
                  <div className="mt-1 text-lg font-semibold text-slate-900">{e.role}</div>
                  <div className="text-[15px] text-slate-600">{e.company}</div>
                  <p className="mt-2 text-[15px] text-slate-500 leading-relaxed max-w-3xl">{e.note}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Education */}
      <section className="border-t border-slate-200 bg-[#0F172A] text-white">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <span className="text-[11px] tracking-[0.22em] uppercase text-slate-400">Education & Certifications</span>
            <h2 className="mt-3 text-3xl lg:text-4xl font-semibold tracking-tight">Trained where it matters.</h2>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { icon: GraduationCap, t: "MBA — Strategy & Marketing", d: "Management Development Institute, Gurgaon (2008)" },
              { icon: GraduationCap, t: "B.E. — Industrial & Production Engineering", d: "Orissa Engineering College, Utkal University (1995)" },
              { icon: Award, t: "Program in Digital Transformation", d: "Indian School of Business (ISB), Hyderabad · 2022" },
              { icon: Award, t: "Roller Bearing Science — Intermediate", d: "Timken University, Canton Ohio · 2001-02" },
            ].map((row, i) => {
              const Icon = row.icon;
              return (
                <div key={i} data-testid={`edu-${i}`} className="border border-white/10 rounded-xl p-5">
                  <Icon className="h-5 w-5 text-[#A78BFA]" />
                  <div className="mt-3 text-base font-semibold">{row.t}</div>
                  <div className="mt-1 text-sm text-slate-400">{row.d}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="mx-auto max-w-[1440px] px-6 lg:px-10 py-20">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 border border-slate-200 rounded-2xl p-8 md:p-10">
          <div>
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <MapPin className="h-4 w-4" /> {BRAND.city}
            </div>
            <h3 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">Want to work with Vivek directly?</h3>
            <p className="mt-2 text-slate-600 max-w-xl">Engagements are limited and hand-picked. Start with a 30-minute discovery call.</p>
          </div>
          <Link to="/contact" data-testid="about-contact-cta" className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white text-sm font-medium px-5 py-3">
            Book discovery call <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
